import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from '@/app/_components/Sheet';
import { ELButton, ELLoader, ELSelect } from '@davincihealthcare/elty-design-system-react';
import Modal from '@/app/_components/modal';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { RecapSection } from '@/app/_components/widgets/listing/cardV3/RecapSection';
import { useAvailabilitySlots } from '@/app/_components/widgets/listing/_hooks/useAvailabilitySlotsV3';
import { SlotAvailability } from '@/app/_components/widgets/listing/types';
import { TimeButton } from '@/app/_components/widgets/listing/cardV3/TimeButton';
import { getDayTitle } from '@/app/_components/widgets/listing/_utils/time';
import { useIsTablet } from '@/app/_hooks/useIsScreen';
import { formatDate } from '@/utils/dates';
import { InlineBanner } from '@/app/_components/AlertBanner';
import { Doctor } from '@/types/algolia-types';
import { getDoctorName } from '@/utils/reservation';
import { SelectDoctorForTabletAndSmaller } from '@/app/_components/widgets/listing/cardV3/SelectDoctorForTabletAndSmaller';

interface AvailabilityModalProps {
  doctors?: Doctor[];
  doctorId?: string;
  firstDayExpected?: number | null;
  initialSelectedDay: number | 'OTHER' | null;
  specialityId: string;
  workgroupId: string;
  onClose: () => void;
  onSubmit: (selectedSlot: SlotAvailability) => void | Promise<void>;
}

const scrollToElement = (id: string) => {
  setTimeout(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
};

export const AvailabilityModalV3: FC<AvailabilityModalProps> = ({
  doctors = [],
  doctorId,
  firstDayExpected,
  initialSelectedDay,
  specialityId,
  workgroupId,
  onClose,
  onSubmit,
}) => {
  const [selectedSlot, setSelectedSlot] = useState<SlotAvailability | null>(null);
  const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(doctorId);

  const firstDayOfAllDoctors = useRef<number | null>(null);

  const { groupedDays, sortedDates, isInitialLoading, fetchNextPage, isLoadingMore, hasMoreData } = useAvailabilitySlots({
    specialityId,
    workgroupId,
    firstAvailabilityDate: firstDayExpected,
    selectedDoctorId,
  });

  const isTabletAndSmaller = useIsTablet();
  /*
    Wait for the first load to complete
    Scroll to the selected day or the first available slot if initialSelectedDay is an existing day
    Scroll to the "show more times" button if initialSelectedDay is OTHER and there are slots available, fetch also additional slots
  */
  useEffect(() => {
    // If the component has been initialized or there are no slots, do nothing
    if (hasBeenInitialized || sortedDates.length === 0 || isInitialLoading) return;

    if (initialSelectedDay && groupedDays[initialSelectedDay]) {
      const firstSlot = groupedDays[initialSelectedDay][0];
      if (firstSlot) {
        setSelectedSlot(firstSlot);
        scrollToElement(`day-${initialSelectedDay}`);
      }
    }

    if (!initialSelectedDay) {
      const firstDay = sortedDates[0];

      if (firstDay && groupedDays[firstDay]) {
        setSelectedSlot(groupedDays[firstDay][0]);
      }
    }

    if (!firstDayOfAllDoctors.current && sortedDates.length > 0) {
      firstDayOfAllDoctors.current = sortedDates[0];
    }

    setHasBeenInitialized(true);
  }, [initialSelectedDay, groupedDays, sortedDates.length, hasBeenInitialized, fetchNextPage, isInitialLoading, sortedDates]);

  const handleLoadMore = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  const handleSubmit = useCallback(async () => {
    if (!selectedSlot) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selectedSlot);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSlot, onSubmit]);

  const continueButton = (
    <ELButton
      label="Continua"
      variant="filled"
      color="primary"
      trailingIcon={<ArrowRightIcon />}
      size="large"
      disabled={!selectedSlot || isSubmitting}
      onClick={handleSubmit}
    />
  );

  const warningMessage = useMemo(() => {
    const firstDay = firstDayOfAllDoctors.current;

    if (!firstDayExpected || firstDayExpected === firstDay || isInitialLoading || !hasBeenInitialized) return;

    if (!firstDay || firstDay > firstDayExpected) {
      return {
        type: 'laterDay',
        message: (
          <p>
            Siamo spiacenti, la prestazione non è più disponibile in data&nbsp;
            <b>
              {formatDate({
                date: new Date(firstDayExpected),
                localeOptions: { day: '2-digit', month: 'long' },
              }).replace(/(\b[a-z])/g, match => match.toUpperCase())}
            </b>
            . Le disponibilità orarie possono variare in base alle prenotazioni fatte da altri utenti.
          </p>
        ),
      };
    }

    if (firstDay && firstDay < firstDayExpected) {
      return {
        type: 'earlierDay',
        message: (
          <p>
            <b>Ottime notizie!</b> C&apos;è una disponibilità anticipata per la prestazione che hai richiesto. Le disponibilità orarie
            possono variare in base alle prenotazioni fatte da altri utenti.
          </p>
        ),
      };
    }
  }, [firstDayExpected, isInitialLoading, hasBeenInitialized]);

  const selectDoctorOptions = useMemo(() => {
    return [
      { value: 'Tutti i professionisti sanitari', id: undefined },
      ...doctors.map(doctor => ({
        value: getDoctorName(doctor),
        id: doctor.id,
      })),
    ];
  }, [doctors]);

  const showSelectDoctor = useMemo(() => {
    return doctors.length > 1 && doctors.some(doctor => doctor.isNameVisible);
  }, [doctors]);

  const doctorSelectLargeScreen = showSelectDoctor && (
    <div className="flex flex-col gap-1">
      <ELSelect
        name="doctors"
        label="Professionista sanitario"
        options={selectDoctorOptions}
        onSelect={option => setSelectedDoctorId(option.id)}
        initialSelectedItem={selectDoctorOptions.find(opt => opt.id === selectedDoctorId)}
        data-cy="doctors-select"
      />
    </div>
  );

  const dayList = (
    <div className="flex flex-col gap-6">
      {warningMessage && (
        <InlineBanner
          title="Disponibilità orarie"
          description={warningMessage.message}
          type={warningMessage.type === 'earlierDay' ? 'info' : 'warning'}
        />
      )}

      {isInitialLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <ELLoader />
        </div>
      ) : (
        <>
          {sortedDates.map(timestamp => {
            const slots = groupedDays[timestamp];

            return (
              <div key={timestamp} id={`day-${timestamp}`} className="flex flex-col gap-3">
                <h3 className="font-semibold capitalize">{getDayTitle(timestamp)}</h3>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(70px,1fr))] gap-2">
                  {slots.map(slot => (
                    <TimeButton
                      key={slot.start}
                      dataCy={`slot-button`}
                      dataTimestamp={slot.start}
                      title={formatDate({
                        date: new Date(slot.start),
                        localeOptions: { hour: '2-digit', minute: '2-digit' },
                      })}
                      isSelected={selectedSlot?.start === slot.start}
                      onClick={() => setSelectedSlot(slot)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex w-full flex-col items-center gap-2 md:flex-row">
            <ELButton
              id="show-more-times"
              aria-label={hasMoreData ? 'Mostra altri orari' : 'Nessun altro orario disponibile'}
              label={hasMoreData ? 'Mostra altri orari' : 'Nessun altro orario disponibile'}
              variant="text"
              disabled={isInitialLoading || isLoadingMore || !hasMoreData}
              color="primary"
              size="small"
              onClick={handleLoadMore}
            />
            {isLoadingMore && <ELLoader />}
          </div>
        </>
      )}
    </div>
  );

  if (isTabletAndSmaller) {
    return (
      <Modal isModalOpen onClose={onClose} className="m-0 h-full max-h-full w-full max-w-full px-4 pt-5">
        <div className="flex h-full max-h-full w-full max-w-full flex-col gap-6">
          <header className="grid grid-cols-[1.5rem_1fr_1.5rem] items-center justify-end text-right">
            <div /> {/*Future back button*/}
            <h2 className="text-center text-base font-semibold text-primary">Scelta orario</h2>
            <button aria-label="Chiudi" className="focus:outline-none" onClick={onClose}>
              <XMarkIcon className="w-full " />
            </button>
          </header>

          <RecapSection selectedSlot={selectedSlot} />

          {doctors.length > 1 && (
            <SelectDoctorForTabletAndSmaller
              selectedDoctorId={selectedDoctorId}
              selectDoctorOptions={selectDoctorOptions}
              onAcceptSelection={setSelectedDoctorId}
            />
          )}

          <div className="scrolling-shadow flex-1 overflow-y-auto pb-28">{dayList}</div>

          <div className="fixed bottom-0 left-0 w-full px-4 [&>button]:w-full">
            <div className="h-6 w-full bg-gradient-to-t from-white via-white/90 to-transparent" />
            {continueButton}
            <div className="h-6 w-full bg-gradient-to-b from-white via-white/90 to-transparent" />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Sheet isOpen={true} onClose={onClose}>
      <div className="relative flex h-full max-h-full flex-col gap-6 px-20 pb-5 pt-10">
        <h2 className="text-2xl font-semibold text-primary">Scelta orario</h2>
        <RecapSection selectedSlot={selectedSlot} />
        {doctorSelectLargeScreen}
        <div className="scrolling-shadow flex-1 overflow-y-auto">{dayList}</div>
        <div className="w-fit">{continueButton}</div>
      </div>
    </Sheet>
  );
};
